import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import merge from 'lodash/merge';
import { translate } from 'react-i18next';

import { formatNumber, getDecimals } from '../../service/market';
import { buildGraphDataUrl, PERIOD_HISTORIC, PERIOD_INTRADAY } from '../../service/oms';

import HighchartsGraph, { defaultOptions } from './HighchartsGraph';
import LoadingComponent from '../LoadingComponent';
import Button from '../Button';
import Link from '../Link';

import css from '../../styles/graph/tickerGraph';
import cssVars from '../../styles/_variables';

const createTooltipFormatter = changeVal => function formatter() {
    let changeHtml = '';

    if (this.series && this.series.processedYData) {
        let color;
        if (this.y === changeVal) {
            color = 'color-neutral';
        } else {
            color = this.y > changeVal ? 'color-positive' : 'color-negative';
        }

        changeHtml = `<span class="change">(<span class="${color}">${Highcharts.numberFormat(
            (this.y - changeVal) / changeVal * 100, 2
        )}%</span>)</span>`;
    }

    const dateFormat = this.x % 10000 === 0 ? '%a %e. %b' : '%a %e. %b %H:%M:%S';

    return (
        `<div class="tooltip-container">
            <div>
                <span>${Highcharts.dateFormat(dateFormat, this.x)}</span>
            </div>
            <div class="tooltip-value">
                <span>${Highcharts.numberFormat(this.y, getDecimals(this.y))} ${changeHtml}</span>
            </div>
        </div>`
    );
};

class TickerGraph extends Component {
    static propTypes = {
        symbol: PropTypes.string.isRequired,
        height: PropTypes.number,
        showButtons: PropTypes.bool,
        close: PropTypes.number,
        TIME: PropTypes.number,
        LAST: PropTypes.number,
        rangeOptions: PropTypes.array,
        t: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props);

        const t = this.props.t;

        this.intervals = [{
            text: t('intraday'),
            range: 'today',
            change: 'CHANGE_PCT',
        }, {
            text: t('week'),
            range: 'oneWeekAgo',
            change: 'CHANGE_7DAYS_PCT',
        }, {
            text: t('month'),
            range: 'oneMonthAgo',
            change: 'CHANGE_MONTH_PCT',
        }, {
            text: t('ytd'),
            range: 'ytd',
            change: 'CHANGE_YEAR_PCT',
        }, {
            text: t('year'),
            range: 'oneYearAgo',
            change: 'CHANGE_1YEAR_PCT',
        }, {
            text: t('yearWithCount', { count: 5 }),
            range: 'fiveYearsAgo',
            change: 'CHANGE_5YEARS_PCT',
        }];

        this.state = {
            dateRange: this.intervals[0].range,
            data: null,
            fetching: false,

            intraday: null,
            historic: null,
        };

        const today = new Date();
        const oneWeekAgo = (new Date()).setDate(today.getDate() - 7);
        const oneMonthAgo = (new Date()).setMonth(today.getMonth() - 1);
        const ytd = new Date(); ytd.setDate(1); ytd.setMonth(0);
        const oneYearAgo = (new Date()).setYear(today.getFullYear() - 1);
        const fiveYearsAgo = (new Date()).setYear(today.getFullYear() - 5);
        const tenYearsAgo = (new Date()).setYear(today.getFullYear() - 10);


        this.dateRanges = {
            today: today.getTime(),
            oneWeekAgo,
            oneMonthAgo,
            ytd: ytd.getTime(),
            oneYearAgo,
            fiveYearsAgo,
            tenYearsAgo,
        };

        this._updateDateState = dateRange => this.setState({ dateRange },
            () => this.fetchData()
        );

        this._updateDateRange = e => {
            e.target.blur();
            this._updateDateState(e.target.getAttribute('value'));
        };

        this.baseConfig = merge({}, defaultOptions, {
            yAxis: {
                plotLines: [{
                    color: cssVars['color-graph-plotline-0'],
                    width: 1,
                    dashStyle: 'longdash',
                    zIndex: 10,
                }],
                labels: {
                    format: '{value:.2f}',
                },
            },
            xAxis: {
                type: 'datetime',
            },
            series: [{
                color: cssVars['color-positive'],
                negativeColor: cssVars['color-negative'],
            }, {
                color: cssVars['color-main-darkest'],
                borderColor: cssVars['color-main-dark'],
                pointWidth: 4,
            }],
        });
    }

    componentDidMount() {
        if (this.props.close) {
            this.fetchData();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.close && !this.state.fetching) {
            this.fetchData();
        }

        if (prevProps.TIME !== this.props.TIME) {
            this.pushData(this.props.TIME, this.props.LAST);
        }

        if (this.state.fetching || this.state.dateRange === 'today') {
            return;
        }

        this.updateColor();
        this.updateMin();
    }

    getActive(value) {
        if (this.state.dateRange === value) {
            return css.rangeOptionActive;
        }

        return css.rangeOption;
    }

    getMinValue() {
        const { dateRange } = this.state;
        if (dateRange === 'today') {
            return undefined;
        }

        return this.dateRanges[dateRange];
    }

    getColor() {
        const { dateRange } = this.state;

        if (dateRange === 'today') {
            return cssVars['color-positive'];
        }

        const map = {
            oneWeekAgo: 'CHANGE_7DAYS',
            oneMonthAgo: 'CHANGE_MONTH',
            ytd: 'CHANGE_YEAR',
            oneYearAgo: 'CHANGE_1YEAR',
            fiveYearsAgo: 'CHANGE_5YEARS',
            tenYearsAgo: 'CHANGE_10YEARS',
        };

        const prop = map[dateRange];
        const value = this.props[prop];

        return value >= 0 ? cssVars['color-positive'] : cssVars['color-negative'];
    }

    pushData(time, last) {
        if (this.chart) {
            const instance = this.chart.getGraphInstance();
            if (instance && instance.series[0]) {
                instance.series[0].addPoint([time, last], true);
            }
        }
    }

    fetchData() {
        const type = this.state.dateRange === 'today' ? 'intraday' : 'historic';
        if (this.state[type] || this.state.fetching) {
            return;
        }

        this.setState({ fetching: true });

        const url = buildGraphDataUrl(
            this.props.symbol, type === 'intraday' ? PERIOD_INTRADAY : PERIOD_HISTORIC
        );

        // eslint-disable-next-line
        fetch(url.graph)
            .then(resp => resp.json())
            .then(data => {
                const serie = this.state.dateRange === 'today' ? 's1' : 'c1';
                let graphData = [];
                if (typeof data.rows !== 'undefined') {
                    graphData = data.rows[0].values.series[serie].data;
                }

                let config = {};
                const threshold = this.props.close || graphData[0][1];

                if (this.state.dateRange === 'today') {
                    config = {
                        yAxis: {
                            plotLines: [{
                                value: threshold,
                                color: cssVars['color-graph-plotline-0'],
                                width: 1,
                                dashStyle: 'longdash',
                                zIndex: 10,
                            }],
                        },
                        series: [{
                            data: graphData,
                            threshold,
                        }],
                        tooltip: {
                            formatter: createTooltipFormatter(threshold),
                        },
                    };
                } else {
                    config = {
                        series: [{
                            data: graphData,
                        }],
                        xAxis: {
                            min: this.getMinValue(),
                        },
                        tooltip: {
                            formatter: createTooltipFormatter(threshold),
                        },
                    };
                }

                const finalConfig = merge(config, this.baseConfig);

                this.setState({ [type]: finalConfig, fetching: false });
            });
        // setting fetch false;
    }

    updateMin() {
        const { dateRange } = this.state;
        if (!this.chart || dateRange === 'today') {
            return;
        }

        const instance = this.chart.getGraphInstance();
        instance.xAxis[0].update({
            min: this.getMinValue(),
        });
    }

    updateColor() {
        const { dateRange } = this.state;
        if (!this.chart || dateRange === 'today') {
            return;
        }

        const instance = this.chart.getGraphInstance();
        instance.series[0].update({
            color: this.getColor(),
        });
    }

    renderChange(n) {
        let statusClass;
        if (!n || n === 0) {
            statusClass = css.neutral;
        } else {
            statusClass = n > 0 ? css.positive : css.negative;
        }

        return (
             <span className={statusClass}>
                {formatNumber(n, 2, true)}
            </span>
        );
    }

    renderRangeOptions() {
        if (!this.props.showButtons) return null;

        let length = this.intervals.length;
        if (typeof this.props.rangeOptions !== 'undefined') {
            length = this.props.rangeOptions.length;
        }

        const buttons = this.intervals.map((interval, i) => {
            if (typeof this.props.rangeOptions !== 'undefined') {
                if (this.props.rangeOptions.indexOf(interval.range) < 0) {
                    return null;
                }
            }

            return (
                <Link
                    metaTitle={['graph:period:', interval.range].join('')}
                    onClick={this._updateDateRange}
                    className={this.getActive(interval.range)}
                    key={i}
                    value={interval.range}
                >
                    <span>{interval.text}</span>
                    {this.renderChange(this.props[interval.change])}
                </Link>
            );
        });

        return (
            <div className={css.rangeOptions} data-num={length}>
                {buttons}
            </div>
        );
    }

    renderNoData() {
        const t = this.props.t;

        return (
            <div className={css.noData}>
                <div>
                    <p>{t('common:noData')}</p>
                    {this.state.dateRange === 'today' && <Button
                        label={t('common:viewHistory')}
                        onClick={() => this._updateDateState('oneMonthAgo')}
                    />}
                </div>
            </div>
        );
    }

    renderGraph() {
        if (this.state.fetching) {
            return <LoadingComponent height={this.props.height} />;
        }

        const config = this.state.dateRange === 'today' ? this.state.intraday : this.state.historic;

        if (!config || !config.series || !config.series[0] || config.series[0].data.length === 0) {
            return this.renderNoData();
        }

        let displayDate = false;
        if (this.state.dateRange === 'today') {
            const dataDate = new Date(config.series[0].data[0][0]);
            const currentDate = new Date();
            if (dataDate.getDate() !== currentDate.getDate()
                || dataDate.getMonth() !== currentDate.getMonth()) {
                displayDate = true;
            }
        }

        return (
            <HighchartsGraph
                className={css.graph}
                config={config}
                firstValue={this.props.close}
                displayDate={displayDate}
                ref={node => { if (node) this.chart = node.getWrappedInstance(); }}
            />);
    }


    render() {
        const height = { height: this.props.height };
        return (
            <div className={css.container}>
                <div className={css.graphWrapper} style={height}>
                    { this.renderGraph() }
                </div>
                <div className={css.rangeOptionsWrapper}>
                    { this.renderRangeOptions() }
                </div>
            </div>
        );
    }
}

export default translate('date')(TickerGraph);



/** WEBPACK FOOTER **
 ** /src/shared/components/graph/TickerGraph.js
 **/